import { useEffect, useState } from "react";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Loader2, Users, Car, Wallet, TrendingUp, Building2, MapPin,
  CheckCircle2, Clock, LogOut, Circle, PlusCircle, Download, History, ArrowDownLeft, ArrowUpRight,
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useNavigate } from "react-router-dom";

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }

function KPICard({ Icon, label, value }) {
  return (
    <Card className="p-4 bg-white border border-[#E4E0D8] rounded-2xl">
      <div className="flex items-center gap-1.5 text-[11px] text-[#5B6B7A] mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-xl font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{value}</div>
    </Card>
  );
}

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rechargeFor, setRechargeFor] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState(5000);
  const [rechargeBusy, setRechargeBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryBusy(true);
    try {
      const { data } = await api.get("/partner/tft-history");
      setHistory(data);
    } catch (e) {
      toast.error("Impossible de charger l'historique");
    } finally { setHistoryBusy(false); }
  };

  const downloadCsv = async (endpoint, filename) => {
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement échoué");
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/partner/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Impossible de charger le tableau de bord"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const handleLogout = () => { logout(); navigate("/"); };

  const submitRecharge = async () => {
    if (!rechargeFor || rechargeAmount <= 0) return;
    if (rechargeAmount > (data?.partner?.tft_balance || 0)) {
      toast.error("Solde flotte insuffisant");
      return;
    }
    setRechargeBusy(true);
    try {
      const { data: resp } = await api.post(`/partner/drivers/${rechargeFor.id}/recharge-tft`, { amount: rechargeAmount });
      toast.success(`+${rechargeAmount.toLocaleString("fr-FR")} F crédités à ${rechargeFor.name}`);
      setRechargeFor(null);
      // Optimistic local update
      setData((d) => ({
        ...d,
        partner: { ...d.partner, tft_balance: resp.partner_new_balance },
        drivers: d.drivers.map((dr) => dr.id === rechargeFor.id ? { ...dr, tft_balance: resp.driver_new_balance } : dr),
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setRechargeBusy(false); }
  };

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F4F2ED]"><Loader2 className="w-8 h-8 animate-spin text-[#0F2B46]" /></div>;
  }

  const { partner, drivers, vehicles, bookings, kpis } = data;

  return (
    <div className="min-h-screen bg-[#F4F2ED] p-4 sm:p-6" data-testid="partner-dashboard">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#0F2B46] text-white flex items-center justify-center">
            <Building2 className="w-6 h-6 text-[#F2A93B]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-[#5B6B7A]">Bienvenue</div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-[#0F2B46] truncate" style={{ fontFamily: "Sora" }}>
              {partner.partner_name}
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={openHistory} className="border-[#CBD5E1]" data-testid="partner-history-btn">
            <History className="w-4 h-4 mr-1" /> Historique TFT
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadCsv("/partner/dashboard-report.csv", "rapport_flotte.csv")} className="border-[#CBD5E1]" data-testid="partner-download-report">
            <Download className="w-4 h-4 mr-1" /> Rapport CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleLogout} className="border-[#CBD5E1]" data-testid="partner-logout">
            <LogOut className="w-4 h-4 mr-1" /> Déconnexion
          </Button>
        </div>

        {/* TFT balance */}
        <Card className="p-4 rounded-2xl bg-gradient-to-br from-[#0F2B46] to-[#173A5E] text-white border-0" data-testid="partner-wallet-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#CFE0EF]">Solde TFT flotte</div>
              <div className="text-3xl sm:text-4xl font-extrabold text-[#F2A93B] mt-1" style={{ fontFamily: "Sora" }} data-testid="partner-tft-balance">{fmtXAF(partner.tft_balance)}</div>
              <div className="text-[11px] text-[#B9CBDA] mt-1">Ce solde alimente toute votre flotte pour les commissions Trigo Express.</div>
            </div>
            <Wallet className="w-8 h-8 text-[#F2A93B]" />
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard Icon={CheckCircle2} label="Courses terminées" value={kpis.total_completed} />
          <KPICard Icon={Clock} label="Courses en cours" value={kpis.total_in_progress} />
          <KPICard Icon={TrendingUp} label="Revenu (100 dernières)" value={fmtXAF(kpis.revenue_xaf)} />
          <KPICard Icon={Users} label="Chauffeurs en ligne" value={`${kpis.online_drivers}/${kpis.total_drivers}`} />
        </div>

        {/* Drivers */}
        <Card className="p-4 rounded-2xl bg-white border border-[#E4E0D8]" data-testid="partner-drivers-card">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-[#F2A93B]" />
            <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Chauffeurs de la flotte</h2>
            <span className="ml-auto text-xs text-[#5B6B7A]"><b className="text-[#0F2B46]">{drivers.length}</b> total</span>
          </div>
          {drivers.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#5B6B7A]">Aucun chauffeur lié à votre flotte pour l&apos;instant.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {drivers.map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-[#E4E0D8]" data-testid={`partner-driver-${d.id}`}>
                  {d.avatar ? (
                    <img src={resolveImageUrl(d.avatar)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#F4F2ED] flex items-center justify-center text-sm font-extrabold text-[#0F2B46]">{(d.name || "?").slice(0, 2).toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-[#0F2B46] text-sm truncate">{d.name}</span>
                      {d.is_online && <Circle className="w-2 h-2 fill-emerald-500 stroke-emerald-500" />}
                    </div>
                    <div className="text-[11px] text-[#5B6B7A]">
                      {d.phone || "—"} · TFT: <b className="text-[#0F2B46]">{fmtXAF(d.tft_balance)}</b>
                    </div>
                  </div>
                  {d.express_enabled && <span className="text-[10px] bg-[#F2A93B]/20 text-[#D98C1F] px-1.5 py-0.5 rounded font-bold">Express</span>}
                  <Button
                    size="sm"
                    onClick={() => { setRechargeFor(d); setRechargeAmount(5000); }}
                    className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold h-8"
                    data-testid={`partner-recharge-driver-${d.id}`}
                  >
                    <PlusCircle className="w-3.5 h-3.5 mr-1" /> Recharger
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Vehicles */}
        <Card className="p-4 rounded-2xl bg-white border border-[#E4E0D8]" data-testid="partner-vehicles-card">
          <div className="flex items-center gap-2 mb-3">
            <Car className="w-5 h-5 text-[#F2A93B]" />
            <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Véhicules de la flotte</h2>
            <span className="ml-auto text-xs text-[#5B6B7A]"><b className="text-[#0F2B46]">{vehicles.length}</b> total</span>
          </div>
          {vehicles.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#5B6B7A]">Aucun véhicule lié.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {vehicles.map((v) => (
                <div key={v.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-[#E4E0D8]" data-testid={`partner-vehicle-${v.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#F4F2ED] flex items-center justify-center">
                    <Car className="w-5 h-5 text-[#0F2B46]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F2B46] text-sm truncate">{v.brand} {v.model}</div>
                    <div className="text-[11px] text-[#5B6B7A]">
                      <span className="font-mono">{v.plate_number || "—"}</span> · {v.vehicle_type || ""}
                    </div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${v.available ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {v.available ? "Disponible" : "Indisponible"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Bookings */}
        <Card className="p-4 rounded-2xl bg-white border border-[#E4E0D8]" data-testid="partner-bookings-card">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-[#F2A93B]" />
            <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Courses récentes de la flotte</h2>
          </div>
          {bookings.length === 0 ? (
            <div className="py-6 text-center text-sm text-[#5B6B7A]">Aucune course pour le moment.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">
                    <th className="py-1.5 pr-2">Chauffeur</th>
                    <th className="py-1.5 pr-2">Client</th>
                    <th className="py-1.5 pr-2">Trajet</th>
                    <th className="py-1.5 pr-2">Type</th>
                    <th className="py-1.5 pr-2">Statut</th>
                    <th className="py-1.5 pr-2 text-right">Montant</th>
                    <th className="py-1.5 pr-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-[#F0EDE6]" data-testid={`partner-booking-row-${b.id}`}>
                      <td className="py-2 pr-2 font-semibold text-[#142433]">{b.driver_name || "—"}</td>
                      <td className="py-2 pr-2">{b.user_name || "—"}</td>
                      <td className="py-2 pr-2 max-w-[220px] truncate" title={`${b.pickup_address} → ${b.dropoff_address}`}>{b.pickup_address} → {b.dropoff_address}</td>
                      <td className="py-2 pr-2">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${b.booking_type === "express" ? "bg-[#F2A93B]/20 text-[#D98C1F]" : "bg-blue-50 text-blue-700"}`}>
                          {b.booking_type === "express" ? "Express" : "Location"}
                        </span>
                      </td>
                      <td className="py-2 pr-2 capitalize">{b.status || "—"}</td>
                      <td className="py-2 pr-2 text-right font-bold text-[#0F2B46]">{fmtXAF(b.total_amount)}</td>
                      <td className="py-2 pr-2 text-[#5B6B7A]">{(b.created_at || "").split("T")[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* TFT History dialog */}
      <Dialog open={historyOpen} onOpenChange={(o) => !o && setHistoryOpen(false)}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="partner-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2B46] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
              <History className="w-5 h-5 text-[#F2A93B]" /> Historique TFT — {history?.partner?.partner_name || ""}
            </DialogTitle>
          </DialogHeader>
          {historyBusy || !history ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="p-3 rounded-xl bg-[#0F2B46] text-white">
                  <div className="text-[10px] uppercase text-[#CFE0EF]">Solde actuel</div>
                  <div className="text-base font-extrabold text-[#F2A93B]" data-testid="history-current-balance">{fmtXAF(history.current_balance)}</div>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50">
                  <div className="text-[10px] uppercase text-emerald-700 flex items-center gap-1"><ArrowDownLeft className="w-3 h-3" />Rechargé (admin)</div>
                  <div className="text-base font-extrabold text-emerald-700" data-testid="history-total-in">+{fmtXAF(history.total_recharged_in)}</div>
                </div>
                <div className="p-3 rounded-xl bg-red-50">
                  <div className="text-[10px] uppercase text-red-700 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />Distribué (chauffeurs)</div>
                  <div className="text-base font-extrabold text-red-700" data-testid="history-total-out">-{fmtXAF(history.total_recharged_out)}</div>
                </div>
              </div>
              <div className="mb-3 flex justify-end">
                <Button size="sm" onClick={() => downloadCsv("/partner/tft-history.csv", "tft_history.csv")} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="history-download-csv">
                  <Download className="w-3.5 h-3.5 mr-1" /> Télécharger CSV
                </Button>
              </div>
              {history.transactions.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucun mouvement TFT.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-left text-[10.5px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">
                        <th className="py-1.5 pr-2">Date</th>
                        <th className="py-1.5 pr-2">Type</th>
                        <th className="py-1.5 pr-2">Chauffeur</th>
                        <th className="py-1.5 pr-2">Note</th>
                        <th className="py-1.5 pr-2 text-right">Montant</th>
                        <th className="py-1.5 pr-2 text-right">Solde après</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.transactions.map((t) => (
                        <tr key={t.id} className="border-b border-[#F0EDE6]" data-testid={`history-tx-row-${t.id}`}>
                          <td className="py-1.5 pr-2 text-[#5B6B7A]">{(t.created_at || "").replace("T", " ").split(".")[0]}</td>
                          <td className="py-1.5 pr-2">
                            {t.amount > 0 ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><ArrowDownLeft className="w-3 h-3" />{t.type_label}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><ArrowUpRight className="w-3 h-3" />{t.type_label}</span>
                            )}
                          </td>
                          <td className="py-1.5 pr-2">{t.counterparty_name || "—"}</td>
                          <td className="py-1.5 pr-2 text-[#5B6B7A] truncate max-w-[180px]" title={t.note}>{t.note}</td>
                          <td className={`py-1.5 pr-2 text-right font-bold ${t.amount > 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {t.amount > 0 ? "+" : ""}{fmtXAF(t.amount)}
                          </td>
                          <td className="py-1.5 pr-2 text-right font-bold text-[#0F2B46]">{fmtXAF(t.running_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Recharge driver TFT modal */}
      <Dialog open={!!rechargeFor} onOpenChange={(o) => !o && setRechargeFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="recharge-driver-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2B46] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
              <Wallet className="w-5 h-5 text-[#F2A93B]" /> Recharger : {rechargeFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-xl bg-[#0F2B46] text-white">
                <div className="text-[10px] uppercase text-[#CFE0EF]">Solde flotte</div>
                <div className="text-base font-extrabold text-[#F2A93B]" data-testid="recharge-partner-balance">{fmtXAF(data?.partner?.tft_balance)}</div>
              </div>
              <div className="p-3 rounded-xl bg-[#F4F2ED]">
                <div className="text-[10px] uppercase text-[#5B6B7A]">Solde chauffeur</div>
                <div className="text-base font-extrabold text-[#0F2B46]" data-testid="recharge-driver-balance">{fmtXAF(rechargeFor?.tft_balance)}</div>
              </div>
            </div>
            <div>
              <label className="text-[10.5px] font-semibold uppercase tracking-wider text-[#5B6B7A] block mb-1">Montant à transférer (F)</label>
              <Input type="number" value={rechargeAmount} onChange={(e) => setRechargeAmount(parseInt(e.target.value || 0))} className="h-9 text-lg font-bold" data-testid="recharge-driver-amount" />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[1000, 2500, 5000, 10000, 25000].map((v) => (
                <Button key={v} type="button" size="sm" variant="outline" onClick={() => setRechargeAmount(v)} className="border-[#CBD5E1] h-7 text-xs" data-testid={`recharge-preset-${v}`}>
                  +{v.toLocaleString("fr-FR")}
                </Button>
              ))}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setRechargeFor(null)} className="border-[#CBD5E1]">Annuler</Button>
              <Button onClick={submitRecharge} disabled={rechargeBusy} className="bg-[#F2A93B] text-[#0F2B46] hover:bg-[#E09520] font-bold" data-testid="recharge-driver-submit">
                {rechargeBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wallet className="w-4 h-4 mr-1" />} Créditer le chauffeur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
